"use client";

import { useEffect, useState } from "react";
import { Table, Button, Card, Spinner } from "react-bootstrap";
import api from "../../services/api";
import { toast } from "react-toastify";
import WalletChargeModal from "./WalletChargeModal";
import CreditAddModal from "./CreditAddModal";
import UserCreateModal from "./UserCreateModal";
import RoleUpdateModal from "./RoleUpdateModal";
import { handleApiSuccess, handleApiError } from "../../services/errorHandler"
import Swal from "sweetalert2";
import { SortAlphaUp, SortAlphaDown, Justify, ArrowDownUp } from "react-bootstrap-icons";
import PaginationBarComp from "../Pagination/PaginationBar";

const AdminUsersComp = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);

  // Modals states
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState(null);

  const [isResetting, setIsResetting] = useState(false);
  const [filter, setFilter] = useState({
    search: "",
    role: "",
    category: "",
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/admin/categories");
      setCategories(data);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "خطا",
        text: "خطا در دریافت لیست گروه‌ها",
      });
    }
  };

  // ✅ Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        ...filter,
        page,
        perPage,
      };
      
      const { data } = await api.get("/admin/users", { params });

      setUsers(data.items || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toast.error("خطا در دریافت کاربران");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 مرتب‌سازی با کلیک روی عنوان ستون
  const handleSort = (column) => {
    if (filter.sortBy === column) {
      // اگر روی همون ستون دوباره کلیک شد، جهت رو برعکس کن
      setFilter({
        ...filter,
        sortOrder: filter.sortOrder === "ASC" ? "DESC" : "ASC",
      });
      setPage(1);
    } else {
      // مرتب‌سازی جدید
      setFilter({
        ...filter,
        sortBy: column,
        sortOrder: "ASC",
      });
      setPage(1);
    }
  };
  
  // 🔹 آیکن جهت مرتب‌سازی
  const SortIcon = ({ column }) => {
    if (filter.sortBy !== column) return <ArrowDownUp size={14} className="ms-1" />;
    return filter.sortOrder === "ASC" ? (
      <SortAlphaDown size={14} className="ms-1" />
    ) : (
      <SortAlphaUp size={14} className="ms-1" />
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [page, perPage, filter]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // ✅ Delete user
  const deleteUser = async (id) => {
    if (!window.confirm("آیا از حذف کاربر اطمینان دارید؟")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("کاربر حذف شد");
      fetchUsers();
    } catch (err) {
      toast.error("خطا در حذف کاربر");
    }
  };

  // ریست پسورد تک کاربر
  const resetUserPassword = async (id) => {
    if (!window.confirm("آیا از ریست پسورد کاربر اطمینان دارید؟ رمز به ۴ رقم آخر کد ملی تغییر خواهد کرد؟")) return;

    try {
      const res = await api.patch(`/admin/users/${id}/reset-password`);
      handleApiSuccess(res, 'عملیات با موفقیت انجام شد');
      //fetchUsers();
    } catch (err) {
      handleApiError(err, "خطا در ثبت درخواست");
    }
  };

  //if (loading)
  //  return <Spinner animation="border" className="d-block m-auto mt-5" />;

  const roleTitles = {
    USER: "کاربر",
    STORE: "فروشگاه",
    ADMIN: "ادمین",
  };

  const categoryColors = [
    { bg: "#EEF2FF", border: "#C7D2FE", text: "#3730A3" }, // indigo
    { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E3A8A" }, // blue
    { bg: "#ECFEFF", border: "#A5F3FC", text: "#155E75" }, // cyan
    { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534" }, // green
    { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46" }, // emerald
    { bg: "#F7FEE7", border: "#D9F99D", text: "#365314" }, // lime
    { bg: "#FFFBEB", border: "#FDE68A", text: "#92400E" }, // yellow
    { bg: "#FFF7ED", border: "#FED7AA", text: "#9A3412" }, // orange
    { bg: "#FFF1F2", border: "#FECDD3", text: "#9F1239" }, // rose
    { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B" }, // red
    { bg: "#FDF4FF", border: "#F5D0FE", text: "#86198F" }, // fuchsia
    { bg: "#FAF5FF", border: "#E9D5FF", text: "#6B21A8" }, // purple
    { bg: "#F5F3FF", border: "#DDD6FE", text: "#5B21B6" }, // violet
    { bg: "#F1F5F9", border: "#CBD5F5", text: "#334155" }, // slate
    { bg: "#F8FAFC", border: "#E2E8F0", text: "#1F2937" }, // gray
    { bg: "#F0FDFA", border: "#99F6E4", text: "#134E4A" }, // teal
    { bg: "#FFF7F5", border: "#FFDAD5", text: "#7C2D12" }, // soft coral
    { bg: "#F5FDF6", border: "#BBF7D0", text: "#14532D" }, // mint
    { bg: "#F8F5FF", border: "#DDD6FE", text: "#4C1D95" }, // deep violet soft
    { bg: "#FDF2F8", border: "#FBCFE8", text: "#9D174D" }, // pink
  ];

  const generalColor = {
    bg: "#FFFFFF",
    border: "#9CA3AF",
    text: "#111827",
  };

  const sumColor = {
    bg: "#312E81",
    border: "#4F46E5",
    text: "#FFFFFF",
  };

  return (
    <Card className="bg-white border-0 rounded-3 mb-4">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-2 g-2 justify-content-start">
            <div className="">
              <input
                type="text"
                className="form-control"
                placeholder="جستجو (نام، موبایل، کد ملی...)"
                value={filter.search}
                onChange={(e) => { setFilter({ ...filter, search: e.target.value }); setPage(1); }}
              />
            </div>

            <div className="">
              <select
                className="form-select"
                value={filter.role}
                onChange={(e) => { setFilter({ ...filter, role: e.target.value}); setPage(1); }}
              >
                <option value="">همه نقش‌ها</option>
                <option value="USER">کاربر</option>
                <option value="STORE">فروشگاه</option>
                <option value="ADMIN">ادمین</option>
              </select>
            </div>

            <div className="">
              <select
                className="form-select"
                value={filter.category}
                onChange={(e) => { setFilter({ ...filter, category: e.target.value}); setPage(1); }}
              >
                <option value="">همه گروه‌ها</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <Button
              title="ریست فیلترها"
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setFilter({
                  search: "",
                  role: "",
                  category: "",
                });
                setPage(1);
              }}
              className="pb-0"
            >
              <i class="material-symbols-outlined">close</i>
            </Button>
          </div>

          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + افزودن کاربر جدید
          </Button>
        </div>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort("code")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  کد
                  <SortIcon column="code" />
                </div>
              </th>
              <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  نام
                  <SortIcon column="name" />
                </div>
              </th>
              <th onClick={() => handleSort("family")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  نام خانوادگی
                  <SortIcon column="family" />
                </div>
              </th>
              <th>کد ملی</th>
              <th>موبایل</th>
              <th onClick={() => handleSort("role")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  نقش
                  <SortIcon column="role" />
                </div>
              </th>
              <th>دسته</th>
              <th>فروشگاه</th>
              <th onClick={() => handleSort("walletBalance")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  کیف پول
                  <SortIcon column="walletBalance" />
                </div>
              </th>
              <th onClick={() => handleSort("creditBalance")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  اعتبار
                  <SortIcon column="creditBalance" />
                </div>
              </th>
              <th onClick={() => handleSort("shopAll")} style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center">
                  مانده حساب جاری
                  <SortIcon column="shopAll" />
                </div>
              </th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center py-4">
                  موردی یافت نشد
                </td>
              </tr>
            ) : (
              users.map((u, idx) => (
                <>
                <tr key={u.id} className="balance-row position-relative">
                  <td>
                    {(page - 1) * perPage + idx + 1}<br/><br/><br/>

                    <div className="w-100 position-absolute" style={{overflowX: 'auto', overflowY: 'hidden', right: '5px', bottom: '5px'}}>
                      <div className="balance-badge d-flex flex-nowrap">
                        {/* مجموع */}
                        <div class="d-flex flex-shrink-0">
                          <div className="balance-col" style={{background: sumColor.bg, borderColor: sumColor.border, color: sumColor.text,}}>مجموع</div>
                          <div className="balance-col" style={{background: sumColor.bg, borderColor: sumColor.border, color: sumColor.text,}}>
                            <div>کیف: {Number(u.totalWalletBalance || 0).toLocaleString()}</div>
                            <div>اعتبار: {Number(u.totalCreditBalance || 0).toLocaleString()}</div>
                          </div>
                        </div>

                        {/* عمومی */}
                        <div class="d-flex flex-shrink-0">
                          <div className="balance-col" style={{background: generalColor.bg, borderColor: generalColor.border, color: generalColor.text,}}>عمومی</div>
                          <div className="balance-col" style={{background: generalColor.bg, borderColor: generalColor.border, color: generalColor.text,}}>
                            <div>کیف: {Number(u.walletBalance || 0).toLocaleString()}</div>
                            <div>اعتبار: {Number(u.creditBalance || 0).toLocaleString()}</div>
                          </div>
                        </div>

                        {/* category ها */}
                        {u.categoryBalances?.map((c, i) => {
                          const color = categoryColors[(c.categoryId - 1) % categoryColors.length];

                          return (
                            <>
                              <div class="d-flex flex-shrink-0">
                                <div className="balance-col" style={{background: color.bg, borderColor: color.border, color: color.text,}}>{c.categoryName}</div>
                                <div className="balance-col" style={{background: color.bg, borderColor: color.border, color: color.text,}}>
                                  <div>کیف: {Number(c.walletBalance || 0).toLocaleString()}</div>
                                  <div>اعتبار: {Number(c.creditBalance || 0).toLocaleString()}</div>
                                </div>
                              </div>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </td>
                  <td>{u.code}</td>
                  <td>{u.name}</td>
                  <td>{u.family}</td>
                  <td>{u.codemelli}</td>
                  <td>{u.mobile}</td>
                  <td>{roleTitles[u.role] ?? u.role}</td>
                  <td>{u.category?.name || "-"}</td>
                  <td>{u.store?.name || "-"}</td>
                  <td>{Number(u.totalWalletBalance || 0).toLocaleString()}</td>
                  <td>{Number(u.totalCreditBalance || 0).toLocaleString()}</td>

                  <td>
                    {u.shopAll ? (
                      <span
                        className={
                          u.shopAll.type === 'charge'
                            ? 'text-success fw-semibold'
                            : 'text-danger fw-semibold'
                        }
                        style={{"display": "inline-block", "direction": "ltr",}}
                      >
                        {/*u.shopAll.type === 'charge' ? 'طلب' : 'بدهی'}{' '*/}
                        {u.shopAll.type === 'charge' ? '' : '-'}
                        {Number(u.shopAll.amount || 0).toLocaleString()}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>

                  <td className="d-flex gap-2 flex-wrap pb-5" style={{"min-height": "38px"}}>
                    { u.role === 'USER' && (
                      <>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setShowChargeModal(true);
                          }}
                        >
                          شارژ کیف پول
                        </Button>
                        <Button
                          size="sm"
                          variant="info"
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setShowCreditModal(true);
                          }}
                        >
                          افزودن اعتبار
                        </Button>
                      </>
                    )}
                    { u.mobile !== '09132085327' && (
                      <>
                        <Button
                          size="sm"
                          variant="warning"
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setShowRoleModal(true);
                          }}
                        >
                          نقش
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setEditUser(u);
                            setShowCreateModal(true);
                          }}
                        >
                          ویرایش
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => deleteUser(u.id)}
                        >
                          حذف
                        </Button>

                        <span
                          title={!u.codemelli || u.codemelli.length < 10 ? "کد ملی صحیح نمی‌باشد" : ""}
                          style={{ cursor: (!u.codemelli || u.codemelli.length < 10) ? "not-allowed" : "pointer" }}
                        >
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => resetUserPassword(u.id)}
                            className="pb-0"
                            disabled={!u.codemelli || u.codemelli.length < 10}
                          >
                            <i className="material-symbols-outlined">key</i>
                          </Button>
                        </span>
                      </>
                    )}
                  </td>
                </tr>
                </>
              ))
            )}
          </tbody>
        </Table>

        <PaginationBarComp
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          perPage={perPage}
          setPerPage={setPerPage}
        />

        {/* Modals */}
        <WalletChargeModal
          show={showChargeModal}
          onHide={() => setShowChargeModal(false)}
          targetType="USER"
          targetId={selectedUserId}
          onSuccess={fetchUsers}
        />

        <CreditAddModal
          show={showCreditModal}
          onHide={() => setShowCreditModal(false)}
          userId={selectedUserId}
          onSuccess={fetchUsers}
        />

        <UserCreateModal
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setEditUser(null);
          }}
          onSuccess={fetchUsers}
          editUser={editUser}
        />

        <RoleUpdateModal
          show={showRoleModal}
          onHide={() => setShowRoleModal(false)}
          userId={selectedUserId}
          onSuccess={fetchUsers}
        />
      </Card.Body>
    </Card>
  );
};

export default AdminUsersComp;
